import createBootError from 'owa-shared-start/lib/createBootError';
import getScopedPath from 'owa-url/lib/getScopedPath';
import getSessionData from 'owa-shared-start/lib/getSessionData';
import { getApp, getOwsPath } from 'owa-config';
import { getConfig, updateServiceConfig } from 'owa-service/lib/config';
import { getCookie } from 'owa-config/lib/universalCookies';
import { logTraceIfParamPresent } from './utils/logTraceIfParamPresent';
import { markEnd, markFunction, markStart, trackBottleneck } from 'owa-performance';
import { onBootComplete } from 'owa-shared-start/lib/onBootComplete';
import { onBootError } from 'owa-shared-start/lib/onBootError';
import { removeLoader } from './utils/removeLoader';
import { setApp } from 'owa-config/lib/bootstrapOptions';
import { setAriaTenantToken } from 'owa-shared-start/lib/ariaUtils';
import { setBootFailureCount } from 'owa-shared-start/lib/bootErrorCounter';
import { setIsDeepLink } from 'owa-url/lib/isDeepLink';
import { unblockLazyLoadCallbacks } from 'owa-bundling-light';
import type StartConfig from './types/StartConfig';
import type { PromiseWithKey } from 'owa-performance-types';
import type { HeadersWithoutIterator } from 'owa-service/lib/RequestOptions';
import type { MailboxInfo } from 'owa-client-types';
import {
    lazyGetAnchorMailbox,
    lazyGetAuthTokenMsal,
    lazyCreateMsalInstance,
    lazyOnActivityTimeoutErrorForMSAL,
    lazyAcquireAccessTokenMsal,
} from 'owa-msaljs/lib/lazyFunctions';
import { isMsalEnabled } from 'mini-common-utils/lib/utils/isMsalEnabled';
import { setAuthTiming } from 'owa-auth-timings';

let startTime: number;

/* eslint-disable-next-line @typescript-eslint/no-explicit-any --
 * See aka.ms/mini-lint-errors
 *	> Unexpected any. Specify a different type. */
export async function miniStart(config: StartConfig): Promise<any> {
    try {
        await internalStart(config);

        try {
            // Unblocking lazy-loading post successful boot
            unblockLazyLoadCallbacks();
            onBootComplete(config, startTime);

            // remove loader screen
            removeLoader();

            // call loader removed callbacks
            config.onLoaderRemoved?.();

            setBootFailureCount(0);
        } catch (error) {
            throw createBootError(error, 'BootComplete');
        }
    } catch (bootError) {
        // Shared boot error logic with big OWA which uses default handleBootError function in owa-shared-start
        onBootError(bootError, config);
    }
}

async function internalStart(config: StartConfig) {
    try {
        startTime = Date.now();
        logTraceIfParamPresent('appStart');

        // don't reset the app if it is already set
        if (!getApp()) {
            // @ts-ignore - The App string is different for Mini
            setApp(config.app);
        }

        setIsDeepLink(config.isDeepLink);

        setAriaTenantToken(config.startupAriaToken);

        if (config.runBeforeStart) {
            markStart('rbsp');
            await config.runBeforeStart(config).catch(error => {
                if (!error.source) {
                    error.source = 'BeforeBoot';
                }
                throw error;
            });
            markEnd('rbsp');
        }

        updateServiceConfig({
            baseUrl: getScopedPath(getOwsPath()),
            appName: config.app,
        });

        let authToken: string | undefined;
        // MSAL creation needs to happen *after* runBeforeStart; in hosted scenarios TeamsJS
        // is initialized in runBeforeStart and sets up the NAA bridge that MSAL uses during init.
        // MSAL should not overwrite any existing getAuthToken callback if one was set up in runBeforeStart,
        // for example hosted auth that relies on Hub SDK and does not yet support MSAL.
        if (isMsalEnabled() && !getConfig().getAuthToken) {
            markStart('msal');
            if (getCookie('msal.cache.encryption')) {
                setAuthTiming('crypt');
            }
            setAuthTiming('msalis');
            // Create the MSAL instance with the correct app-specific parameters here in order to
            // be able to make the startupdata request before we start evaluating other boot bundles
            await lazyCreateMsalInstance.importAndExecute(config.msalConfiguration);
            setAuthTiming('msalie');

            setAuthTiming('fgatmsals');
            authToken = await lazyGetAuthTokenMsal.importAndExecute();
            setAuthTiming('fgatmsalsc');

            const getAuthToken = (headers?: HeadersWithoutIterator, mailboxInfo?: MailboxInfo) =>
                lazyGetAuthTokenMsal.importAndExecute(headers, mailboxInfo);

            const getMsalToken = (
                mailboxInfo: MailboxInfo,
                apiName: string,
                resource?: string,
                scope?: string,
                correlationId?: string,
                wwwAuthenticateHeader?: string
            ) =>
                lazyAcquireAccessTokenMsal.importAndExecute(
                    mailboxInfo,
                    apiName,
                    resource,
                    scope,
                    undefined /* headers */,
                    correlationId,
                    wwwAuthenticateHeader
                );

            updateServiceConfig({
                getMsalToken,
                getAuthToken,
                getAnchorMailbox: (mailboxInfo?: MailboxInfo) =>
                    lazyGetAnchorMailbox.importAndExecute(mailboxInfo),
                onAuthFailed: () => {},
                onActivityTimeoutError: () => lazyOnActivityTimeoutErrorForMSAL.importAndExecute(),
                isMsalFlowEnabled: true,
            });
            markEnd('msal');
        }

        const sessionDataPromise =
            config.overrideBootPromises?.(authToken) ?? getSessionData(authToken);

        const javascriptPromise = markFunction(function bootstrap() {
            return config.bootstrap.import();
        }, 'mjs')();

        /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
         * See aka.ms/mini-lint-errors
         *	> Unexpected any. Specify a different type. */
        const bootPromises: PromiseWithKey<any>[] = [
            { promise: sessionDataPromise, key: 'sd' },
            { promise: javascriptPromise, key: 'js' },
        ];

        // TODO: Do we need getOpxHostData? Since it's already included in bootstrap flow

        /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
         * See aka.ms/mini-lint-errors
         *	> Unexpected any. Specify a different type. */
        let optionsPromise: Promise<any> = Promise.resolve();
        const options = config.options;
        if (options) {
            const promise = markFunction(() => options.import(), 'ojs')();
            bootPromises.push({ promise, key: 'ojs' });

            optionsPromise = options.import().then(optionsFunc => optionsFunc(sessionDataPromise));
        }

        const bootstrapPromises = [javascriptPromise, optionsPromise];

        bootPromises.push({
            promise: Promise.all(bootstrapPromises)
                .then(([bootFunc, bootOptions]) => {
                    try {
                        /* eslint-disable-next-line prefer-spread --
                         * undefined
                         *	> Use the spread operator instead of '.apply()'. */
                        return bootFunc.apply(
                            null,
                            bootOptions ? [bootOptions] : [sessionDataPromise]
                        );
                    } catch (e) {
                        throw createBootError(e, 'Bootstrap');
                    }
                })
                .catch(e => {
                    throw createBootError(e, 'Script');
                }),
            key: null,
        });

        if (config.runAfterRequests) {
            config.runAfterRequests(sessionDataPromise);
        }

        return trackBottleneck('start', bootPromises);
    } catch (error) {
        return Promise.reject(createBootError(error, 'Preboot'));
    }
}
